import { useCallback, useMemo } from 'react';
import logger from '../../../Services/LoggingService';
import { ApiService } from '../../../Services/ApiService';
import { IPHEPlateType } from '../../../Models/IPHEPlateType';
import { IUnitConfiguration } from '../../../Models/IUnitConfiguration';
import { getUnitConfigurationKeyAsDashedString, getUnitConfigurationFromConfigurationKey } from '../../../Utils/UnitConfigurationHelper';
import { AvailableProductLine } from '../../../Models/Enums';

/**
 * Props interface for PHE Plate Type API operations
 */
interface PHEPlateTypeApiOperationsProps {
  setCurrentData: (data: IPHEPlateType[]) => void;
  setDisabledForPositions: (data: any[]) => void;
  setIsLoading: (loading: boolean) => void;
  setIsDeleting: (deleting: string) => void;
  setShowDeleteConfirmation: (confirmation: string) => void;
  setMasterListOfAvailablePositions: (positions: string[]) => void;
  setAvailablePositions: (positions: string[]) => void;
  currentData: IPHEPlateType[];
  setUnitConfigData: (data: any[]) => void;
  unitConfigData: any[];
}

/**
 * Hook for PHE Plate Type API operations
 */
export const usePHEPlateTypeApiOperations = (
  apiService: ApiService,
  stateProps: PHEPlateTypeApiOperationsProps
) => {
  const {
    setCurrentData,
    setDisabledForPositions,
    setIsLoading,
    setIsDeleting,
    setShowDeleteConfirmation,
    setMasterListOfAvailablePositions,
    setAvailablePositions,
    currentData,
    setUnitConfigData,
    unitConfigData
  } = stateProps;

  const getAllUnitConfigs = useCallback(async () => {
    try {
      const response = await apiService.getAsync('settings/unitconfigurations');
      const data = await response.json();
      
      let allowedPositions: string[] = [];
      
      if (data.length > 0) {
        allowedPositions = data.map((x: IUnitConfiguration) => getUnitConfigurationKeyAsDashedString(x));
      }
      setMasterListOfAvailablePositions(allowedPositions);
      setAvailablePositions(allowedPositions);
      setUnitConfigData(data);
    } catch (error) {
      logger.error("Failed to fetch unit configurations", error as Error, null, {
        component: 'PHEPlateTypeManagement',
        method: 'getAllUnitConfigs'
      });
    }
  }, [apiService, setMasterListOfAvailablePositions, setAvailablePositions, setUnitConfigData]);

  const getAllPHETypes = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getAsync('settings/pheplatetypes');
      const data = await response.json();
      
      let disabledFor: any[] = [];
      for (let i = 0; i < data.length; i++) {
        const element = data[i];
        const disabledDataResponse = await apiService.getAsync(`settings/pheplatetypes/availability/${element.key}`);
        const disData = await disabledDataResponse.json();
        disabledFor.push(disData);
      }
      setDisabledForPositions(disabledFor);
      setCurrentData(data);
    } catch (error) {
      logger.error("Failed to fetch PHE types", error as Error, null, {
        component: 'PHEPlateTypeManagement',
        method: 'getAllPHETypes'
      });
    } finally {
      setIsLoading(false);
    }
  }, [apiService, setIsLoading, setDisabledForPositions, setCurrentData]);

  const getPHETypeAvailability = useCallback(async (key: string) => {
    try {
      const disDataResponse = await apiService.getAsync(`settings/pheplatetypes/availability/${key}`);
      const disData = await disDataResponse.json();
      
      let disAllowedpos: string[] = [];
      let allowedPos: string[] = [];
      
      if (disData.length > 0) {
        for (let i = 0; i < disData.length; i++) {
          const element = disData[i];
          let config: IUnitConfiguration = {
            industry: element.industry,
            subIndustry: element.subIndustry,
            application: element.application,
            subApplication: element.subApplication,
            productLine: element.productLine,
            processStage: element.processStage,
            alfaLavalPosition: element.alfaLavalPosition,
            hotMediaType: '',
            coldMediaType: '',
            breakdownOfProcessStage: '',
            inputs: [],
            nonUsedProperties: [],
            flowType: '',
            outputs: [],
            prefferedUnit: '',
            samplingId: ''
          };
          
          const configString: string = getUnitConfigurationKeyAsDashedString(config);
          if (!element.isAvailable) {
            disAllowedpos.push(configString);
          } else {
            allowedPos.push(configString);
          }
        }
      }

      return { disabledPositions: disAllowedpos, availablePositions: allowedPos };
    } catch (error) {
      logger.error("Failed to fetch PHE type availability", error as Error, null, {
        component: 'PHEPlateTypeManagement',
        method: 'getPHETypeAvailability'
      });
      throw error;
    }
  }, [apiService]);

  const deletePheType = useCallback(async (item: IPHEPlateType) => {
    try {
      setIsDeleting(item.key);

      const response = await apiService.delete(`settings/pheplatetypes/delete/${item.key}`, {
        industry: item.industry,
        subIndustry: item.subIndustry,
        application: item.application,
        subApplication: item.subApplication,
        processStage: item.processStage,
        productLine: item.productLine,
        alfaLavalPosition: item.alfaLavalPosition,
        breakdownOfProcessStage: '',
        coldMediaType: '',
        hotMediaType: '',
        inputs: [],
        nonUsedProperties: []
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Could not delete PHE plate type. ${errorText}`);
      }

      const newPhePlateTypes = currentData.filter((p: IPHEPlateType) => p.key !== item.key);
      setCurrentData(newPhePlateTypes);
    } catch (error) {
      logger.error("Failed to delete PHE plate type", error as Error, null, {
        component: 'PHEPlateTypeManagement',
        method: 'deletePheType'
      });
    } finally {
      setShowDeleteConfirmation('');
      setIsDeleting('');
    }
  }, [apiService, currentData, setIsDeleting, setCurrentData, setShowDeleteConfirmation]);

  const savePhePlateType = useCallback(async (
    pheTypeActive: IPHEPlateType | undefined,
    text: string,
    key: string,
    availableInALICE: boolean,
    infoForALICE: string,
    industrial: boolean,
    hygienic: boolean,
    selectedPositions: string[]
  ) => {
    try {
      let availableProductLine;
      if (industrial) {
        availableProductLine = AvailableProductLine.Industrial;
      } else if (hygienic) {
        availableProductLine = AvailableProductLine.Hygienic;
      }

      const body: any = {};
      
      if (pheTypeActive === undefined) {
        body.PhePlateData = {
          text: text,
          key: key,
          isAvailableInALICE: availableInALICE,
          informationForUser: infoForALICE,
          AvailableProductLine: availableProductLine
        };
      } else {
        body.PhePlateData = {
          id: pheTypeActive.id,
          text: text,
          key: pheTypeActive.key,
          isAvailableInALICE: availableInALICE,
          informationForUser: infoForALICE,
          AvailableProductLine: availableProductLine
        };
      }

      body.UnitConfigurationsToDisallow = selectedPositions.map((x) => {
        const foundConfig = unitConfigData.find((config: IUnitConfiguration) => 
          getUnitConfigurationKeyAsDashedString(config) === x
        );

        if (foundConfig) {
          return foundConfig;
        }

        // Fallback to parsing key if not found in data (should rarely happen if state is synced)
        const unitConfigFromKey: IUnitConfiguration = getUnitConfigurationFromConfigurationKey(x);
        return {
          industry: unitConfigFromKey.industry,
          subIndustry: unitConfigFromKey.subIndustry,
          application: unitConfigFromKey.application,
          subApplication: unitConfigFromKey.subApplication,
          processStage: unitConfigFromKey.processStage,
          productLine: unitConfigFromKey.productLine,
          alfaLavalPosition: unitConfigFromKey.alfaLavalPosition,
          // Keep existing fields empty/default as we don't have real data
          hotMediaType: unitConfigFromKey.hotMediaType || '',
          coldMediaType: unitConfigFromKey.coldMediaType || '',
          flowType: unitConfigFromKey.flowType || '',
          // We avoid hardcoding specific values like "Water" or "Performance" as per user request.
          // Ideally, all configs should be found in unitConfigData.
        };
      });

      const response = await apiService.postAsync(body, `settings/pheplatetypes`);
      if (!response.ok) {
        const res = await response.text();
        throw Error(`Could not send response. ${response.status} ${res}`);
      }

      await getAllPHETypes();
    } catch (error) {
      logger.error("Failed to save PHE plate type", error as Error, null, {
        component: 'PHEPlateTypeManagement',
        method: 'savePhePlateType'
      });
      // Re-throw error so handleFormSubmit can handle isSaving state properly
      throw error;
    }
  }, [apiService, getAllPHETypes, unitConfigData]);

  return useMemo(() => ({
    getAllUnitConfigs,
    getAllPHETypes,
    getPHETypeAvailability,
    deletePheType,
    savePhePlateType
  }), [getAllUnitConfigs, getAllPHETypes, getPHETypeAvailability, deletePheType, savePhePlateType]);
}; 